<script setup lang="ts">
import { computed, inject, ref, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { chargeClosuresApi, type ChargeClosure } from '@/api/charge-closures';
import { taxDocumentsApi } from '@/api/tax-documents';
import { type SerializedUser } from '@/api/users';
import { usePermissions } from '@/composables/usePermissions';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import { apiErrorText } from '@/utils/api-errors';
import { formatCurrency } from '@/utils/currency';
import {
  closureStateChip, closureState, CLOSURE_STATES, type ClosureState,
  documentKindLabel, centsToAmount, taxDocumentProblem,
} from '@/utils/tax-documents';
import { formatShortDate } from '@/utils/patient-status';
import { mayClip } from '@/utils/names';
import ChargeClosureDetailDialog from '@/components/charge-closures/charge-closure-detail-dialog.vue';
import ResponsiveDialog from '@/components/responsive-dialog.vue';

// Every closure of the company with what the tax authority answered: the place
// to pick up an emission that did not go through (retry, emit an approved
// closure that has no live document) or to void a closure that was never issued.
const { t } = useI18n();
const { hasPermission } = usePermissions();
const { tableProps } = useResponsiveTable({ desktopItemsPerPage: 25 });
const queryClient = useQueryClient();
const currentUser = inject<Ref<SerializedUser>>('currentUser');

const canEmit = computed(() => (
  hasPermission.value('charges', 'emit') && !!currentUser?.value?.companyIssuesTaxDocuments
));
// The stored charges PDF is the patient's charge detail, guarded by charges/show
// like its download from Cobros.
const canDownloadChargesPdf = computed(() => hasPermission.value('charges', 'show'));

const stateFilter = ref<ClosureState | null>(null);
const search = ref('');
const stateOptions = computed(() => CLOSURE_STATES
  .map((state) => ({ value: state, title: t(`chargeClosures.state.${state}`) })));

// A document is the provider's for a while after Emitir: the list keeps
// asking until every one has settled, so the number shows up without a reload.
const PENDING_STATUSES = ['draft', 'submitted'];
const PENDING_REFETCH_MS = 20_000;
function emissionPending(closure: ChargeClosure): boolean {
  return !!closure.taxDocument && PENDING_STATUSES.includes(closure.taxDocument.status);
}

const { data: closures, isFetching } = useQuery({
  queryKey: ['chargeClosures'],
  queryFn: () => chargeClosuresApi.index(),
  initialData: [] as ChargeClosure[],
  refetchInterval: (query) => (
    (query.state.data ?? []).some(emissionPending) ? PENDING_REFETCH_MS : false
  ),
});

function refresh() {
  queryClient.invalidateQueries({ queryKey: ['chargeClosures'] });
}

const successMessage = ref('');
const errorMessage = ref('');
const successSnackbar = ref(false);
const errorSnackbar = ref(false);

function notify(messageKey: string) {
  successMessage.value = t(messageKey);
  successSnackbar.value = true;
  refresh();
}

function report(error: unknown, fallbackKey: string) {
  errorMessage.value = apiErrorText(error, t(fallbackKey));
  errorSnackbar.value = true;
}

const { mutateAsync: emitDocument, isPending: emitting } = useMutation({
  mutationFn: (closure: ChargeClosure) => taxDocumentsApi.create(closure.id),
  onSuccess: () => notify('chargeClosures.list.emitted'),
  onError: (error) => report(error, 'chargeClosures.list.emitError'),
});

const { mutateAsync: retryDocument, isPending: retrying } = useMutation({
  mutationFn: (closure: ChargeClosure) => taxDocumentsApi.retry(closure.taxDocument!.id),
  onSuccess: () => notify('chargeClosures.list.retried'),
  onError: (error) => report(error, 'chargeClosures.list.emitError'),
});

// Voiding an emitted closure goes through a credit note the authority must
// accept; the row keeps its "issued" state until then.
const reverseCandidate = ref<ChargeClosure | null>(null);
const showReverseDialog = ref(false);
const { mutateAsync: reverseDocument, isPending: reversing } = useMutation({
  mutationFn: (closure: ChargeClosure) => taxDocumentsApi.reverse(closure.taxDocument!.id),
  onSuccess: () => notify('chargeClosures.list.reversed'),
  onError: (error) => report(error, 'chargeClosures.list.reverseError'),
});

function askReverse(closure: ChargeClosure) {
  reverseCandidate.value = closure;
  showReverseDialog.value = true;
}

async function confirmReverse() {
  if (!reverseCandidate.value) return;
  showReverseDialog.value = false;
  await reverseDocument(reverseCandidate.value).catch(() => undefined);
}

const reverseDeadlinePassed = computed(() => {
  const until = reverseCandidate.value?.taxDocument?.reversibleUntil;

  return !!until && until < new Date().toISOString().slice(0, 'YYYY-MM-DD'.length);
});

const voidCandidate = ref<ChargeClosure | null>(null);
const showVoidDialog = ref(false);
const { mutateAsync: voidClosure, isPending: voiding } = useMutation({
  mutationFn: (closure: ChargeClosure) => chargeClosuresApi.void(closure.id),
  onSuccess: () => notify('chargeClosures.list.voided'),
  onError: (error) => report(error, 'chargeClosures.list.voidError'),
});

function askVoid(closure: ChargeClosure) {
  voidCandidate.value = closure;
  showVoidDialog.value = true;
}

async function confirmVoid() {
  if (!voidCandidate.value) return;
  showVoidDialog.value = false;
  await voidClosure(voidCandidate.value).catch(() => undefined);
}

const selectedClosureId = ref<number | null>(null);
const showDetailDialog = ref(false);
function openDetail(closure: ChargeClosure) {
  selectedClosureId.value = closure.id;
  showDetailDialog.value = true;
}

const busy = computed(() => emitting.value || retrying.value || voiding.value || reversing.value);

// The closure summary carries the live document or, failing that, the latest
// resolved one. An authority rejection (or a withdrawn draft) needs a new
// document and leaves the closure voidable; a failed emission is retried under
// its idempotency key — the provider may hold it — so its closure stays put.
const RESOLVED_STATUSES = ['rejected', 'cancelled'];

function documentResolved(closure: ChargeClosure): boolean {
  return !closure.taxDocument || RESOLVED_STATUSES.includes(closure.taxDocument.status);
}
function canEmitFor(closure: ChargeClosure): boolean {
  return canEmit.value && closure.status === 'approved' && documentResolved(closure);
}
function canRetryFor(closure: ChargeClosure): boolean {
  return canEmit.value && closure.taxDocument?.status === 'failed';
}
function canVoidFor(closure: ChargeClosure): boolean {
  return canEmit.value && closure.status === 'approved' && documentResolved(closure);
}
function canReverseFor(closure: ChargeClosure): boolean {
  const document = closure.taxDocument;

  return canEmit.value && closure.status === 'issued' && document?.status === 'accepted' &&
    document.kind !== 'credit_note';
}

function money(closure: ChargeClosure, cents: number): string {
  return formatCurrency(centsToAmount(cents, closure.currency));
}

function documentTitle(closure: ChargeClosure): string {
  const document = closure.taxDocument;
  if (!document) return '';
  const label = documentKindLabel(document.kind, document.documentCode, t);

  return document.number ? `${label} N° ${document.number}` : label;
}

function searchableText(closure: ChargeClosure): string {
  return [closure.patientName, closure.recipientName, closure.recipientTaxId, closure.taxDocument?.number]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

const filteredClosures = computed(() => {
  const term = search.value.trim().toLowerCase();

  return closures.value.filter((closure: ChargeClosure) => (
    (!stateFilter.value || closureState(closure) === stateFilter.value) &&
    (!term || searchableText(closure).includes(term))
  ));
});

// The columns that must never wrap: a total split over two lines, a folio
// orphaned from its document or an action icon dropped to a second row all come
// from a long name elsewhere stealing the width.
const NO_WRAP = { class: 'text-no-wrap' };

const headers = computed(() => [
  { title: t('chargeClosures.list.patient'), key: 'patientName' },
  { title: t('chargeClosures.list.period'), key: 'periodStart', cellProps: NO_WRAP },
  { title: t('chargeClosures.list.recipient'), key: 'recipientName', sortable: false },
  { title: t('chargeClosures.list.total'), key: 'totalAmountCents', align: 'end' as const,
    cellProps: NO_WRAP },
  { title: t('chargeClosures.list.document'), key: 'taxDocument', sortable: false,
    cellProps: NO_WRAP },
  { title: t('chargeClosures.list.state'), key: 'state', sortable: false },
  { title: '', key: 'actions', sortable: false, align: 'end' as const, cellProps: NO_WRAP },
]);
</script>

<template>
  <v-card class="w-100">
    <v-toolbar
      color="white"
      flat
    >
      <v-toolbar-title class="font-weight-bold">
        {{ t('chargeClosures.list.title') }}
      </v-toolbar-title>
    </v-toolbar>
    <v-card-text>
      <p class="text-body-2 text-medium-emphasis mb-2">
        {{ t('chargeClosures.list.window') }}
      </p>
      <v-row class="align-center">
        <v-col
          cols="12"
          sm="6"
          md="4"
        >
          <v-text-field
            v-model="search"
            :label="t('chargeClosures.list.search')"
            prepend-inner-icon="mdi-magnify"
            variant="outlined"
            density="compact"
            hide-details
            clearable
          />
        </v-col>
        <v-col
          cols="12"
          sm="6"
          md="3"
        >
          <v-select
            v-model="stateFilter"
            :items="stateOptions"
            :label="t('chargeClosures.list.state')"
            variant="outlined"
            density="compact"
            hide-details
            clearable
          />
        </v-col>
        <v-spacer />
        <v-col
          cols="12"
          md="auto"
        >
          <v-btn
            href="/charges"
            variant="text"
            color="primary"
            prepend-icon="mdi-arrow-left"
          >
            {{ t('chargeClosures.list.backToCharges') }}
          </v-btn>
        </v-col>
      </v-row>
      <v-data-table
        v-bind="tableProps"
        :headers="headers"
        :header-props="{ class: 'font-weight-bold' }"
        :items="filteredClosures"
        :loading="isFetching"
        :loading-text="t('chargeClosures.list.loading')"
        :no-data-text="t('chargeClosures.list.empty')"
        :sort-by="[{ key: 'periodStart', order: 'desc' }]"
        class="mt-4"
      >
        <template #item.periodStart="{ item }">
          {{ formatShortDate(item.periodStart) }} – {{ formatShortDate(item.periodEnd) }}
        </template>
        <template #item.patientName="{ item }">
          <div class="name-cell text-truncate">
            {{ item.patientName }}
          </div>
          <v-tooltip
            v-if="mayClip(item.patientName)"
            activator="parent"
            location="top"
          >
            {{ item.patientName }}
          </v-tooltip>
        </template>
        <template #item.recipientName="{ item }">
          <div class="name-cell text-truncate">
            {{ item.recipientName }}
          </div>
          <div class="text-caption text-medium-emphasis text-no-wrap">
            {{ item.recipientTaxId }}
          </div>
          <v-tooltip
            v-if="mayClip(item.recipientName)"
            activator="parent"
            location="top"
          >
            {{ item.recipientName }}
          </v-tooltip>
        </template>
        <template #item.totalAmountCents="{ item }">
          {{ money(item, item.totalAmountCents) }}
        </template>
        <template #item.taxDocument="{ item }">
          <span v-if="item.taxDocument">{{ documentTitle(item) }}</span>
          <span
            v-else
            class="text-medium-emphasis"
          >
            {{ t('chargeClosures.list.noDocument') }}
          </span>
        </template>
        <!-- One state, not two: how far the period got, whatever the closure and
             its document each say about themselves. A failed or rejected one
             explains itself on hover. -->
        <template #item.state="{ item }">
          <v-chip
            :color="closureStateChip(item, t).color"
            size="small"
          >
            {{ closureStateChip(item, t).label }}
            <v-tooltip
              v-if="item.taxDocument && taxDocumentProblem(item.taxDocument, t)"
              activator="parent"
              location="top"
              max-width="360"
            >
              {{ taxDocumentProblem(item.taxDocument, t) }}
            </v-tooltip>
          </v-chip>
        </template>
        <!-- Every action is an icon with its name on hover, raised like the
             Cobros row. They all look alike on purpose: the tooltip says which
             is which, and both anular actions ask for confirmation anyway. -->
        <template #item.actions="{ item }">
          <v-btn
            icon
            size="small"
            class="me-1"
            :aria-label="t('chargeClosures.list.detail')"
            @click="openDetail(item)"
          >
            <v-icon icon="mdi-eye" />
            <v-tooltip
              activator="parent"
              location="top"
            >
              {{ t('chargeClosures.list.detail') }}
            </v-tooltip>
          </v-btn>
          <v-btn
            v-if="item.pdfAvailable && canDownloadChargesPdf"
            icon
            size="small"
            class="me-1"
            :aria-label="t('chargeClosures.list.chargesPdf')"
            :href="chargeClosuresApi.pdfUrl(item.id)"
            target="_blank"
            rel="noopener noreferrer"
          >
            <v-icon icon="mdi-printer" />
            <v-tooltip
              activator="parent"
              location="top"
            >
              {{ t('chargeClosures.list.chargesPdf') }}
            </v-tooltip>
          </v-btn>
          <v-btn
            v-if="item.taxDocument?.pdfAvailable"
            icon
            size="small"
            class="me-1"
            :aria-label="t('chargeClosures.list.documentPdf')"
            :href="taxDocumentsApi.pdfUrl(item.taxDocument.id)"
            target="_blank"
            rel="noopener noreferrer"
          >
            <v-icon icon="mdi-file-document-outline" />
            <v-tooltip
              activator="parent"
              location="top"
            >
              {{ t('chargeClosures.list.documentPdf') }}
            </v-tooltip>
          </v-btn>
          <v-btn
            v-if="canEmitFor(item)"
            icon
            size="small"
            class="me-1"
            :aria-label="t('chargeClosures.list.emit')"
            :disabled="busy"
            @click="emitDocument(item).catch(() => undefined)"
          >
            <v-icon icon="mdi-file-document-check-outline" />
            <v-tooltip
              activator="parent"
              location="top"
            >
              {{ t('chargeClosures.list.emit') }}
            </v-tooltip>
          </v-btn>
          <v-btn
            v-if="canRetryFor(item)"
            icon
            size="small"
            class="me-1"
            :aria-label="t('chargeClosures.list.retry')"
            :disabled="busy"
            @click="retryDocument(item).catch(() => undefined)"
          >
            <v-icon icon="mdi-refresh" />
            <v-tooltip
              activator="parent"
              location="top"
            >
              {{ t('chargeClosures.list.retry') }}
            </v-tooltip>
          </v-btn>
          <v-btn
            v-if="canReverseFor(item)"
            icon
            size="small"
            class="me-1"
            :aria-label="t('chargeClosures.list.reverse')"
            :disabled="busy"
            @click="askReverse(item)"
          >
            <v-icon icon="mdi-file-document-minus-outline" />
            <v-tooltip
              activator="parent"
              location="top"
            >
              {{ t('chargeClosures.list.reverse') }}
            </v-tooltip>
          </v-btn>
          <v-btn
            v-if="canVoidFor(item)"
            icon
            size="small"
            class="me-1"
            :aria-label="t('chargeClosures.list.void')"
            :disabled="busy"
            @click="askVoid(item)"
          >
            <v-icon icon="mdi-undo-variant" />
            <v-tooltip
              activator="parent"
              location="top"
            >
              {{ t('chargeClosures.list.void') }}
            </v-tooltip>
          </v-btn>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>

  <charge-closure-detail-dialog
    v-model="showDetailDialog"
    :closure-id="selectedClosureId"
  />

  <responsive-dialog
    v-model="showVoidDialog"
    :max-width="480"
    :title="t('chargeClosures.list.voidTitle')"
  >
    <template #default>
      <p v-if="voidCandidate">
        {{ t('chargeClosures.list.voidBody', {
          patient: voidCandidate.patientName ?? '',
          from: formatShortDate(voidCandidate.periodStart),
          to: formatShortDate(voidCandidate.periodEnd),
        }) }}
      </p>
    </template>
    <template #actions="{ close }">
      <v-btn
        variant="text"
        @click="close"
      >
        {{ t('chargeClosures.cancel') }}
      </v-btn>
      <v-btn
        color="error"
        @click="confirmVoid"
      >
        {{ t('chargeClosures.list.void') }}
      </v-btn>
    </template>
  </responsive-dialog>

  <responsive-dialog
    v-model="showReverseDialog"
    :max-width="520"
    :title="t('chargeClosures.list.reverseTitle')"
  >
    <template #default>
      <template v-if="reverseCandidate?.taxDocument">
        <p>
          {{ t('chargeClosures.list.reverseBody', {
            document: documentTitle(reverseCandidate),
            patient: reverseCandidate.patientName ?? '',
            from: formatShortDate(reverseCandidate.periodStart),
            to: formatShortDate(reverseCandidate.periodEnd),
          }) }}
        </p>
        <v-alert
          v-if="reverseDeadlinePassed"
          type="warning"
          variant="tonal"
          density="compact"
          class="mt-3"
        >
          {{ t('chargeClosures.list.reverseDeadlinePassed', {
            date: formatShortDate(reverseCandidate.taxDocument.reversibleUntil),
          }) }}
        </v-alert>
      </template>
    </template>
    <template #actions="{ close }">
      <v-btn
        variant="text"
        @click="close"
      >
        {{ t('chargeClosures.cancel') }}
      </v-btn>
      <v-btn
        color="error"
        @click="confirmReverse"
      >
        {{ t('chargeClosures.list.reverseConfirm') }}
      </v-btn>
    </template>
  </responsive-dialog>

  <v-snackbar
    v-model="successSnackbar"
    :timeout="4000"
    color="primary"
    rounded="pill"
  >
    {{ successMessage }}
  </v-snackbar>
  <v-snackbar
    v-model="errorSnackbar"
    :timeout="5000"
    color="error"
    rounded="pill"
  >
    {{ errorMessage }}
  </v-snackbar>
</template>

<style scoped>
/* Names get a column they cannot outgrow; the tooltip carries the rest. */
.name-cell {
  max-width: 240px;
}
</style>
